import React, { useContext, useEffect, useState } from 'react';
import {
  Card,
  Checkbox,
  Col,
  InputNumber,
  List,
  notification,
  Row,
  Space,
  Tabs,
  Image,
  Popconfirm,
  Button,
  Typography,
} from 'antd';
import Meta from 'antd/lib/card/Meta';
import {
  AssetCatalogResponse,
  duplicateAssetCatalog,
  getAssetCatalogByID,
  ParAssetCatalogOptionResponse,
  ParAssetCatalogServiceResponse,
} from 'services/assetconfig';
import { toCurrency } from 'utils/utils';
import ModalController from 'components/ModalController';
import { FormInstance } from 'antd/lib/form/Form';
import VirtualList from 'rc-virtual-list';
import { PlusOutlined } from '@ant-design/icons';
import { AuthContext } from 'components/Auth/Auth';
import { useHistory } from 'react-router-dom';
import DuplicateButtonAction from 'components/DuplicatButtonAction';
import TasksListPopover from 'pages/workflow/TasksListPopover';
import IActions from 'icons/IActions';
import ModalControllerNV from 'components/ModalControllerNV';
import AssetCatalogSingleItemDetailModal from 'components/AssetCatalogModal/AssetCatalogSingleItemDetailModal';
import GetLabel from 'DisplayFramework/components/GetLabel';
import { loadFileAsResource } from 'services/Document';

const { TabPane } = Tabs;

export type CardProps = {
  item: AssetCatalogResponse;
  onSelect: (
    item: AssetCatalogResponse,
    nbasset: number,
    selectedServices: ParAssetCatalogServiceResponse[],
    selectedOption: ParAssetCatalogOptionResponse[]
  ) => void;
  form: FormInstance;
  myCatalog?: boolean;
  refetch?: any;
  hideServiceAndOptions?: boolean;
};

const AssetTitle: React.FC<{
  listAsset: AssetCatalogResponse;
  item: AssetCatalogResponse;
}> = ({ listAsset, item }) => (
  <div
    style={{
      width: '100%',
      minWidth: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      display: 'block',
      wordBreak: 'break-word',   // ← retour à la ligne
      whiteSpace: 'normal', 
    }}
    title={listAsset?.assetlabel}
  >
    <strong>
      <ModalControllerNV
        content={({ setVisible }) => (
          <a
            onClick={() => setVisible(true)}
            style={{
              display: 'block',
              overflow: 'hidden',
              whiteSpace: 'normal',
              textOverflow: 'ellipsis',
              maxWidth: '100%',
              direction: 'ltr',
              textAlign: 'left',
            }}
          >
            {listAsset?.assetlabel}
          </a>
        )}
        modal={({ setVisible, visible }) => (
          <AssetCatalogSingleItemDetailModal
            currcode={item?.currcode}
            item={listAsset}
            visible={visible}
            onCancel={() => setVisible(false)}
            onSelect={() => {}}
            preview
          />
        )}
      />
    </strong>
  </div>
);

const AssetCover: React.FC<{
  ImageBase64: string;
  item: AssetCatalogResponse;
  listAsset: AssetCatalogResponse;
  myCatalog?: boolean;
  onPreview: (catalogid: number) => void;
  useAntImage?: boolean;
}> = ({
  ImageBase64,
  item,
  listAsset,
  myCatalog,
  onPreview,
  useAntImage = true,
}) => {
  const history = useHistory();

  const imgStyle: React.CSSProperties = {
    width: '100%',
    height: '180px',
    objectFit: 'contain',
    display: 'block',
    background: '#fafafa',
    cursor: 'pointer',
  };

  return (
    <ModalController>
      {({ setVisible, visible }) => (
        <>
          {useAntImage ? (
            <Image
              preview={{ visible: false }}
              src={'data:image/jpeg;charset=utf-8;base64, ' + ImageBase64}
              style={imgStyle}
              onClick={(e) => {
                setVisible(true);
                e.stopPropagation();
                myCatalog
                  ? history.push(
                      `/configuration/asset-catalog/${item.catalogid}/catalog`
                    )
                  : onPreview(Number(listAsset?.catalogid));
              }}
            />
          ) : (
            <img
              alt={listAsset?.assetlabel}
              src={'data:image/jpeg;charset=utf-8;base64, ' + ImageBase64}
              style={imgStyle}
              onClick={(e) => {
                setVisible(true);
                e.stopPropagation();
                myCatalog
                  ? history.push(
                      `/configuration/asset-catalog/${item.catalogid}/catalog`
                    )
                  : onPreview(Number(listAsset?.catalogid));
              }}
            />
          )}
          <AssetCatalogSingleItemDetailModal
            currcode={item?.currcode}
            item={listAsset}
            visible={visible}
            onCancel={() => setVisible(false)}
            onSelect={() => {}}
            preview
          />
        </>
      )}
    </ModalController>
  );
};

const PriceDisplay: React.FC<{
  item: AssetCatalogResponse;
  assetnetamountWithOptions?: number;
  assetnetamountorigineWithOptions?: number;
  showOptionsPrice?: boolean;
}> = ({
  item,
  assetnetamountWithOptions,
  assetnetamountorigineWithOptions,
  showOptionsPrice,
}) => {
  const currcode = item?.currcode ?? localStorage.getItem('currcode') ?? '';
  const hasDiscount =
    item?.assetnetamountorigine > item?.assetdiscountamount &&
    item?.assetdiscountperc !== 0 &&
    item?.assetdiscountperc != null;

  if (hasDiscount) {
    const discountedAmount = showOptionsPrice
      ? (assetnetamountorigineWithOptions ?? item.assetnetamountorigine) -
        item?.assetdiscountamount
      : item.assetnetamountorigine - item?.assetdiscountamount;

    return (
      <div>
        <Row style={{ color: 'red' }} justify="end">
          {'-' + item.assetdiscountperc + '%'}
        </Row>
        <Row justify="center" style={{ color: 'red' }}>
          <strong
            style={{ fontSize: '25px', textAlign: 'center', color: 'red' }}
          >
            {toCurrency(discountedAmount, currcode)}
          </strong>
        </Row>
        <Row style={{ color: '#293c72' }}>
          <GetLabel label="Old Amount" />:
          {toCurrency(item.assetnetamountorigine, currcode)}
        </Row>
      </div>
    );
  }

  const displayAmount =
    showOptionsPrice && assetnetamountWithOptions != null
      ? assetnetamountWithOptions
      : item?.assetnetamount || item.assetnetamountorigine;

  return (
    <div>
      <div>&nbsp;</div>
      <Row justify="center">
        <strong
          style={{
            marginRight: '2%',
            fontSize: '20px',
            textAlign: 'center',
            color: 'red',
          }}
        >
          {toCurrency(displayAmount, currcode)}
        </strong>
      </Row>
      <div style={{ height: 30 }}>&nbsp;</div>
    </div>
  );
};

const CatalogActions: React.FC<{
  item: AssetCatalogResponse;
  isInspect: boolean;
  redirectPath?: string;
}> = ({ item, isInspect, redirectPath }) => {
  const history = useHistory();
  const target =
    redirectPath ?? `/configuration/asset-catalog/${item.catalogid}`;

  return (
    <Space
      direction="horizontal"
      style={{ display: 'flex', justifyContent: 'center' }}
    >
      <Col style={{ marginTop: '3px' }}>
        <Popconfirm
          title="Sure to duplicate?"
          onConfirm={(e: any) => {
            e?.stopPropagation();
            !isInspect &&
              duplicateAssetCatalog({
                catalogid: item.catalogid,
                catalogidorigine: 0,
                isnewVersion: false,
              }).then((res) => {
                history.push(
                  `/configuration/asset-catalog/${res.data.catalogid}/catalog`
                );
                notification.success({ message: res.message });
              });
          }}
        >
          <DuplicateButtonAction comcode="duplicate" />
        </Popconfirm>
      </Col>
      <Col style={{ marginTop: '3px' }}>
        <Popconfirm
          title="Sure to create a new version ?"
          onConfirm={() => {
            !isInspect &&
              duplicateAssetCatalog({
                catalogid: item.catalogid,
                catalogidorigine: item.catalogidorigine ?? item.catalogid,
                isnewVersion: true,
              }).then((res) => {
                history.push(
                  `/configuration/asset-catalog/${res.data.catalogid}/catalog`
                );
                notification.success({ message: res.message });
              });
          }}
        >
          <DuplicateButtonAction comcode="newVersion" />
        </Popconfirm>
      </Col>
    </Space>
  );
};

//::::card styles ::::
const innerCardStyle: React.CSSProperties = {
  borderRadius: '5px',
  width: '100%',
  maxWidth: '100%',
  overflow: 'hidden',
};

const Cards = (props: CardProps) => {
  const { item, onSelect, myCatalog, refetch, hideServiceAndOptions } = props;
  const { isInspect } = useContext(AuthContext);
  const history = useHistory();

  const listAsset = item;
  const [nbasset, setnbasset] = useState(1);
  const [activeKey, setActiveKey] = useState('1');
  const [selectedServices, setSelectedServices] = useState<
    ParAssetCatalogServiceResponse[]
  >([]);
  const [selectedOption, setSelectedOption] = useState<
    ParAssetCatalogOptionResponse[]
  >([]);
  const [ImageBase64, setImageBase64] = useState<string>('');

  const [assetnetamountWithOptions, setAssetnetamountWithOptions] = useState(
    listAsset.assetnetamount
  );
  const [
    assetnetamountorigineWithOptions,
    setAssetnetamountorigineWithOptions,
  ] = useState(listAsset.assetnetamountorigine);
  const [assettaxamountWithOptions, setAssettaxamountWithOptions] = useState(
    listAsset.assettaxamount
  );
  const [assetgrossamountWithOptions, setAssetgrossamountWithOptions] =
    useState(listAsset.assetgrossamount);

  useEffect(() => {
    loadFileAsResource({ filename: item.assetpictureurl }).then((result) => {
      setImageBase64(result.description);
    });
  }, [item]);

  const handlePreviewCATALOG = (catalogid: number) => {
    getAssetCatalogByID({ catalogid })
      .then((response) => {
        if (response.code !== '200') {
          notification.error({ message: response.message });
        }
      })
      .catch((response) => {
        notification.error({ message: response.message });
      });
  };

  /** Common "Add" button rendered in every tab's actions */
  const AddButton = (extraItem: Partial<AssetCatalogResponse> = {}) => (
    <Typography.Link
      key={1}
      onClick={() =>
        onSelect(
          {
            ...item,
            assetnetamountorigine: item.assetnetamount,
            assetgrossamountorigine: item.assetgrossamount,
            assetnetamount: assetnetamountWithOptions,
            assettaxamount: assettaxamountWithOptions,
            assetgrossamount: assetgrossamountWithOptions,
            assetmulticomponents:
              selectedOption.length > 0 ? true : item?.assetmulticomponents,
            ...extraItem,
          },
          nbasset,
          selectedServices,
          selectedOption
        )
      }
    >
      <Button icon={<PlusOutlined />} type="primary">
        <GetLabel label="add" />
      </Button>
    </Typography.Link>
  );

  const QuantityInput = (
    <InputNumber
      key={2}
      size="middle"
      value={nbasset}
      min={1}
      max={100}
      onChange={(nb) => setnbasset(nb ?? 0)}
      style={{ width: '70%' }}
    />
  );

  //:::: Tab 1: Asset::::
  const AssetTab = (
    <TabPane tab="Asset" key="1" animated>
      <Row justify="center">
        <Card
          className="cardcatalog"
          bordered={false}
          style={innerCardStyle}
          title={<AssetTitle listAsset={listAsset} item={item} />}
          cover={
            <AssetCover
              ImageBase64={ImageBase64}
              item={item}
              listAsset={listAsset}
              myCatalog={myCatalog}
              onPreview={handlePreviewCATALOG}
              useAntImage
            />
          }
          actions={[!myCatalog && QuantityInput, AddButton()].filter(Boolean)}
        >
          <Meta
            title={<PriceDisplay item={item} />}
            description={
              myCatalog && (
                <CatalogActions
                  item={item}
                  isInspect={isInspect}
                  redirectPath={`/configuration/asset-catalog/${item.catalogid}/catalog`}
                />
              )
            }
          />
        </Card>
      </Row>
    </TabPane>
  );

  //:::: Tab 2: Services::::
  const ServicesTab = (
    <TabPane tab="Services" key="2">
      <Row justify="center">
        <Card
          bordered={false}
          className="cardcatalog"
          style={innerCardStyle}
          title={<AssetTitle listAsset={listAsset} item={item} />}
          cover={
            <AssetCover
              ImageBase64={ImageBase64}
              item={item}
              listAsset={listAsset}
              myCatalog={myCatalog}
              onPreview={handlePreviewCATALOG}
              useAntImage={false}
            />
          }
          actions={[
            !myCatalog && QuantityInput,
            AddButton({
              assetmulticomponents: selectedOption.length > 0 ? true : false,
            }),
          ].filter(Boolean)}
        >
          {item?.assetCatalogServices?.length > 0 && (
            <List>
              <VirtualList
                data={item?.assetCatalogServices ?? []}
                height={120}
                itemKey="id"
                itemHeight={20}
              >
                {(e: any) => (
                  <List.Item style={{ padding: '8px 1px' }}>
                    <Checkbox
                      value={e.servcode}
                      onChange={(a) => {
                        if (a.target.checked) {
                          setSelectedServices([...selectedServices, e]);
                        } else {
                          setSelectedServices(
                            selectedServices.filter(
                              (i) => i.servcode !== a.target.value
                            )
                          );
                        }
                      }}
                    >
                      <Col>
                        {e.servlabel +
                          ' : ' +
                          toCurrency(e.servfixedamount, item.currcode)}
                      </Col>
                    </Checkbox>
                  </List.Item>
                )}
              </VirtualList>
            </List>
          )}
          <Meta
            title={<PriceDisplay item={item} />}
            description={
              myCatalog && <CatalogActions item={item} isInspect={isInspect} />
            }
          />
        </Card>
      </Row>
    </TabPane>
  );

  //:::: Tab 3: Options::::
  const OptionsTab = (
    <TabPane tab="Options" key="3">
      <Row justify="center">
        <Card
          bordered={false}
          className="cardcatalog"
          style={innerCardStyle}
          title={<AssetTitle listAsset={listAsset} item={item} />}
          cover={
            <AssetCover
              ImageBase64={ImageBase64}
              item={item}
              listAsset={listAsset}
              myCatalog={myCatalog}
              onPreview={handlePreviewCATALOG}
              useAntImage
            />
          }
          actions={[
            !myCatalog && QuantityInput,
            AddButton({
              assetnetamountorigine: assetnetamountorigineWithOptions,
              assetmulticomponents: selectedOption.length > 0,
            }),
          ].filter(Boolean)}
        >
          {item?.assetCatalogOptions?.length > 0 && (
            <List>
              <VirtualList
                data={item?.assetCatalogOptions ?? []}
                height={120}
                itemKey="id"
                itemHeight={20}
              >
                {(e: any) => (
                  <List.Item>
                    <Checkbox
                      value={e.optvalue}
                      onChange={(a) => {
                        const checked = a.target.checked;
                        const sign = checked ? 1 : -1;
                        setSelectedOption(
                          checked
                            ? [...selectedOption, e]
                            : selectedOption.filter(
                                (i) => i.optvalue !== a.target.value
                              )
                        );
                        setAssetnetamountWithOptions(
                          (v) => v + sign * e.optnetamount
                        );
                        setAssettaxamountWithOptions(
                          (v) => v + sign * e.opttaxamount
                        );
                        setAssetnetamountorigineWithOptions(
                          (v) => v + sign * e.optnetamount
                        );
                        setAssetgrossamountWithOptions(
                          (v) => v + sign * e.optgrossamount
                        );
                      }}
                    >
                      <Col>
                        {e.optcodelabel +
                          ' : ' +
                          e.optvaluelabel +
                          ' : ' +
                          toCurrency(e.optnetamount, item.currcode)}
                      </Col>
                    </Checkbox>
                  </List.Item>
                )}
              </VirtualList>
            </List>
          )}
          <Meta
            title={
              <PriceDisplay
                item={item}
                assetnetamountWithOptions={assetnetamountWithOptions}
                assetnetamountorigineWithOptions={
                  assetnetamountorigineWithOptions
                }
                showOptionsPrice
              />
            }
            description={
              myCatalog && <CatalogActions item={item} isInspect={isInspect} />
            }
          />
        </Card>
      </Row>
    </TabPane>
  );

  return (
    <Card
      style={{
        alignContent: 'center',
        boxShadow: '0px 0px 7px rgb(0 0 0 / 9%)',
        overflow: 'hidden', // ← prevents the inner card from bleeding out
      }}
    >
      <Tabs
        activeKey={activeKey}
        centered
        onChange={setActiveKey}
        tabBarExtraContent={
          myCatalog && (
            <TasksListPopover
              entityId={item.catalogid}
              entityReference={'ID : ' + item?.catalogid}
              entityName="CATALOG"
              title={item.assetlabel}
              onClose={() => refetch?.()}
            >
              <Button
                icon={<IActions height="15px" width="15px" />}
                size="middle"
              />
            </TasksListPopover>
          )
        }
      >
        {AssetTab}
        {!hideServiceAndOptions && ServicesTab}
        {!hideServiceAndOptions && OptionsTab}
      </Tabs>
    </Card>
  );
};

export default Cards;
